import { CampaignHeaderSection } from '~/shared/components/cards/CampaignItemsCard';
import { useContext, useEffect, useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import {
    fetchFundRequestVendors,
    populateVendors,
    deleteVendor,
} from '~/services/fundrequests';
import { FundRequestVendor } from '~/types/fundrequests';
import PayeesTable from './PayeesTable';
import { AdminContext } from '~/context/admin';
import { Button } from 'reactstrap';
import { CampaignContext } from '~/context/campaign';
import { AuthContext } from '~/context/auth';
import { fetchMyCampaign } from '~/services/campaign';
import { MyCampaignResponse } from '~/types/campaign';
import { Loading } from '~/shared/components/Loading';

const Payees = () => {
    const [payees, setPayees] = useState<FundRequestVendor[]>([]);
    const [{ campaign }, campaignDispatch] = useContext(CampaignContext);
    const [{ user }, userDispatch] = useContext(AuthContext);
    const [{ isAdmin, campaignId }, adminDispatch] = useContext(AdminContext);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [isLoading, setIsLoading] = useState(false);
    const [loading, setLoading] = useState(true);
    const [fetchingCampaign, setFetchingCampaign] = useState(false);
    const mountedRef = useRef(true);
    const history = useHistory();

    useEffect(() => {
        if (campaign && mountedRef.current) {
            setLoading(false);
        }
    }, [user, campaign]);

    useEffect(() => {
        if (!campaign || !campaign.campaignDetail) {
            if (user?.is_superuser) {
                return;
            }
            if (!fetchingCampaign) {
                setFetchingCampaign(true);
                getMyCampaign();
            }
        }
        return () => {
            mountedRef.current = false;
        };
    }, [user, campaign]);

    const getMyCampaign = async () => {
        try {
            const resp: MyCampaignResponse = await fetchMyCampaign(campaignId);
            if (resp.detail) {
                // Delete localStorage token
                localStorage.removeItem('token');
                campaignDispatch({
                    type: 'set_campaign',
                    payload: null,
                });
                history.push('/login');
                return;
            } else {
                campaignDispatch({
                    type: 'set_campaign',
                    payload: resp,
                });
                const { person, role } = resp;
                userDispatch({
                    type: 'set_person',
                    person,
                    role,
                });
                setLoading(false);
            }
        } catch (e) {
            console.log('getMyCampaign() e: ', e);
            // Show some error here
        }
    };

    const getPayeesList = async (page = 1, pageSize: number) => {
        try {
            setIsLoading(true);
            const payeesList = await fetchFundRequestVendors({
                page,
                pageSize,
                campaignId,
                sort: "desc"
            });
            setPayees(payeesList || []);
            // setTotalPages(Math.ceil(payeesList.count / pageSize) || 1);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (campaign?.campaignDetail) {
            getPayeesList(currentPage, pageSize);
        }
    }, [currentPage, pageSize, campaign?.campaignDetail]);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handlePopulateVendors = async () => {
        try {
            setIsLoading(true);
            await populateVendors(campaignId);
            getPayeesList(currentPage, pageSize);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteVendor = async (vendorId: number): Promise<void> => {
        const response = await deleteVendor(vendorId, campaignId);
        if (response.ok) {
            // Refresh the payees list after successful deletion
            await getPayeesList(currentPage, pageSize);
        } else {
            console.error('Failed to delete vendor:', response.statusText);
            throw new Error('Failed to delete payee. Please try again.');
        }
    };

    const ntafId = campaign?.campaignDetail?.ntaf_id;

    function Heading() {
        return (
            <div>
                <h1 className='h2'>Payees</h1>
                <div>
                    {ntafId && payees.length === 0 && (
                        <Button
                            color='primary'
                            size='sm'
                            onClick={handlePopulateVendors}
                            disabled={isLoading}
                        >
                            {isLoading
                                ? `Importing Payees...`
                                : `Import Payees`}
                        </Button>
                    )}
                </div>
            </div>
        );
    }

    if (!campaign || loading) {
        return <Loading />;
    }

    return (
        <>
            <div className='container__inner'>
                <CampaignHeaderSection leftcard={<Heading />} />
                <PayeesTable
                    payees={payees}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    onPageChange={handlePageChange}
                    isLoading={isLoading}
                    onDelete={handleDeleteVendor}
                />
            </div>
        </>
    );
};

export default Payees;
